import Image from "next/image";

import { ChevronRightCircle } from "lucide-react";

import { Button } from "@repo/ui/components/ui/button";
import type { BackgroundImageCard as BackgroundImageCardType } from "@repo/ui/types/cms";

export type BackgroundImageCardProps = {
  card: BackgroundImageCardType;
  testId?: string;
};

export default function BackgroundImageCard({ card, testId }: Readonly<BackgroundImageCardProps>) {
  const btn = card.button?.[0];

  // Extract Button variant logic into a variable
  let buttonVariant:
    | "default"
    | "link"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "warning" = "default";

  if (
    ["default", "link", "destructive", "outline", "secondary", "ghost", "warning"].includes(
      btn?.variant?.[0]?.codename ?? "default",
    )
  ) {
    buttonVariant = btn?.variant?.[0]?.codename as
      | "default"
      | "link"
      | "destructive"
      | "outline"
      | "secondary"
      | "ghost"
      | "warning";
  }

  return (
    <div
      className="relative flex min-h-44 w-full flex-col overflow-hidden rounded-2xl border px-4 py-2"
      data-testid={testId ? testId : "background-image-card-section"}
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        {card.image && (
          <Image
            loading="lazy"
            src={card.image}
            fill
            sizes="(min-width: 768px) 400px, 320px"
            className="object-cover"
            alt={card.title}
          />
        )}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,white_40%,transparent_100%)]" />
      </div>

      <div className="text-brand-blue relative z-10 flex h-full flex-col">
        <div>
          <p className="text-2xl font-bold">{card.title}</p>
          {card.subtitle && <p className="text-brand-black/70 w-6/12">{card.subtitle}</p>}
        </div>

        {btn && (
          <Button
            variant={buttonVariant}
            size={(btn.size?.[0]?.codename as "default" | "sm" | "lg" | "xl") ?? "default"}
            className="mt-auto w-fit gap-2"
            onClick={() =>
              btn.url &&
              window.open(btn.url, btn.openinnewtab?.[0]?.codename === "yes" ? "_blank" : "_self")
            }
          >
            {btn.label} <ChevronRightCircle size={16} />
          </Button>
        )}
      </div>
    </div>
  );
}
